'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowPathIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { baseURL } from '@utils/data-fetching/utils';
import { fetcher } from '@utils/data-fetching/fetcher';
import type { ApiSchema } from '@type/api/apiUtilityFn';

interface ProjectEditSidebarProps {
  jobSlug: string;
  initialJob?: ApiSchema<'JobOutput'> | null;
}

/**
 * @description Sidebar component for viewing project details and managing publication status.
 * Edit functionality is handled via the job-add/edit page.
 */
export const ProjectEditSidebar = ({ jobSlug, initialJob }: ProjectEditSidebarProps) => {
  const { t } = useTranslation();
  const [job, setJob] = useState<ApiSchema<'JobOutput'> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Publication state
  const [isPublished, setIsPublished] = useState(false);
  const [isPublishLoading, setIsPublishLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Initialize from initialJob if provided, otherwise fetch
  useEffect(() => {
    if (initialJob) {
      setJob(initialJob);
      setIsPublished(initialJob.isPublished || false);
      setIsLoading(false);
      return;
    }

    const fetchJob = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${baseURL}/v1/screening/jobs/${jobSlug}`);
        if (!response.ok) {
          return;
        }
        const data = await response.json() as ApiSchema<'JobOutput'>;
        setJob(data);
        setIsPublished(data.isPublished || false);
      } catch {
        // Error handling - silently fail
      } finally {
        setIsLoading(false);
      }
    };

    void fetchJob();
  }, [jobSlug, initialJob]);

  const handleEditClick = () => {
    // Open job edit page in new tab
    window.open(`/job-add/edit/${jobSlug}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ArrowPathIcon className="size-8 animate-spin text-ink-light" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Project Content */}
      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        {/* Job Title with Edit Link */}
        {job?.title && (
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold text-ink">{job.title}</h2>
            <button
              type="button"
              onClick={handleEditClick}
              className="flex items-center gap-1 rounded-lg border border-input bg-surface px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:border-secondary hover:bg-secondary/5"
              title={t('screening.edit.job', 'Edit Job')}
            >
              <PencilIcon className="size-4" />
              <ArrowTopRightOnSquareIcon className="size-3" />
            </button>
          </div>
        )}

        {/* Job Description - Read-only */}
        {job?.textContent && (
          <div>
            <label className="mb-2 block text-sm font-medium text-ink">
              {t('screening.project.description.label', 'Description')}
            </label>
            <div className="max-h-[200px] min-h-[100px] overflow-y-auto rounded-xl border border-input bg-surface-50 px-4 py-3 text-sm text-ink">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: job.textContent }}
              />
            </div>
          </div>
        )}

        {/* Screening Questions - Read-only */}
        {job?.screeningQuestions && job.screeningQuestions.length > 0 && (
          <div>
            <label className="mb-2 block text-sm font-medium text-ink">
              {t('screening.questions.title', 'Screening Questions')}
            </label>
            <div className="space-y-2">
              {job.screeningQuestions.map((question, index) => (
                question && (
                  <div
                    key={`question-${index}`}
                    className="rounded-lg border border-input bg-surface-50 px-4 py-2 text-sm text-ink"
                  >
                    {index + 1}. {question}
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* External Applies Section */}
        <div className="border-t border-surface-200 pt-6">
          <h3 className="mb-1 text-sm font-medium text-ink">
            {t('screening.externalApplies.title', 'External Applies')}
          </h3>
          <p className="mb-4 text-xs text-ink-light">
            {t('screening.external_applicants.link.desc', 'Allow candidates to apply directly via a public link')}
          </p>

          {/* Publish Toggle */}
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm text-ink">
              {isPublished
                ? t('screening.externalApplies.published', 'Published')
                : t('screening.externalApplies.notPublished', 'Not Published')}
            </span>
            <button
              type="button"
              disabled={isPublishLoading}
              onClick={async () => {
                if (!job?.jobSlug) return;
                setIsPublishLoading(true);
                try {
                  const endpoint = isPublished
                    ? `/v1/screening/jobs/${job.jobSlug}/unpublish`
                    : `/v1/screening/jobs/${job.jobSlug}/publish`;
                  await fetcher(endpoint, undefined, { method: 'POST' });
                  setIsPublished(!isPublished);
                } catch {
                  // Silently fail
                } finally {
                  setIsPublishLoading(false);
                }
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                isPublished ? 'bg-secondary' : 'bg-surface-300'
              } ${isPublishLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`pointer-events-none inline-block size-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isPublished ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Apply URL with Copy */}
          {job?.jobSlug && (
            <div className="space-y-2">
              <label className="block text-xs text-ink-light">
                {t('screening.externalApplies.applyUrl', 'Apply URL')}
              </label>
              <input
                type="text"
                readOnly
                value={`${typeof window !== 'undefined' ? window.location.origin : ''}/apply-screening/${job.jobSlug}`}
                className="w-full rounded-lg border border-input bg-surface-50 px-3 py-2 text-sm text-ink"
              />
              <button
                type="button"
                onClick={() => {
                  const url = `${window.location.origin}/apply-screening/${job?.jobSlug || ''}`;
                  void navigator.clipboard.writeText(url);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
                className={`flex w-full items-center justify-center gap-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  linkCopied
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
                    : 'border-input bg-surface hover:bg-surface-100'
                }`}
              >
                <ClipboardDocumentIcon className="size-4" />
                {linkCopied ? t('global.clipboard.link-copied', 'Copied!') : t('company.detail.referral.copy', 'Copy')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectEditSidebar;
